import React, { useState } from "react";
import { useModalState } from "../../helpers/ModalProvider";
import axios from "axios";
import { getApiUrl } from "../../config";
import { env } from "../../helpers/GlobalStateProvider";
import { getToken } from "../../helpers/tokenManager";
import { DialogBody, DialogCloseTrigger, DialogContent, DialogFooter, DialogHeader, DialogRoot, DialogTitle } from "../../components/ui/dialog";
import { Text } from "@chakra-ui/react";
import { Button } from "../../components/ui/button";

const VerifyEmailModal: React.FC = () => {
  const { isVerifyEmailModalVisible, closeVerifyEmailModal, pendingVerifyEmail, showErrorMessage } =
    useModalState();

  const [isLoading, setIsLoading] = useState(false);
  const [resent, setResent] = useState(false);

  /**
   * Resends the confirmation for a pending email change.
   *
   * This posts to request_change_email, the same endpoint that started the
   * change. The previous target — request_verify_new_email — does not exist in
   * restapi and never has, so this button would have 404'd even with an
   * address; the unset local `email` state short-circuited it before the
   * request could reveal that.
   *
   * Re-calling is safe: sendSettingsVerificationEmail mints a fresh random
   * token into Redis under its own key with a 24h TTL, so a resend simply
   * issues a second valid link rather than invalidating the first.
   */
  const resendChangeEmailConfirmation = async (enteredEmail: string) => {
    const bearerToken = getToken();
    if (!bearerToken) throw new Error("You appear to be signed out. Please sign in and try again.");

    const response = await axios.post(
      `${getApiUrl(env)}/v3/apiKey/request_change_email`,
      { email: enteredEmail },
      { headers: { Authorization: `Bearer ${bearerToken}` } },
    );
    return response.data;
  };

  const handleResendLink = async () => {
    if (!pendingVerifyEmail) return;
    setIsLoading(true);
    setResent(false);
    try {
      await resendChangeEmailConfirmation(pendingVerifyEmail);
      setResent(true);
    } catch (err: any) {
      if (process.env.NODE_ENV === 'development') console.error(err);
      // Previously the failure was swallowed entirely, so a failed resend was
      // indistinguishable from a successful one.
      showErrorMessage(
        err?.response?.data?.message ||
          err?.message ||
          "Could not resend the confirmation email. Please try again.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const onCancel = () => {
    setResent(false);
    closeVerifyEmailModal();
  };

  return (
    <DialogRoot lazyMount open={isVerifyEmailModalVisible} onOpenChange={(e) => { if (!e.open) onCancel(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Check your inbox</DialogTitle>
          <DialogCloseTrigger />
        </DialogHeader>
        <DialogBody>
          <Text fontSize="md" color="#52525b" lineHeight="24px">
            Click the confirm link in your email to confirm your new email
            address{pendingVerifyEmail ? ", " : "."}
            {pendingVerifyEmail && (
              <Text as="span" fontWeight="medium" color="#18181b">
                {pendingVerifyEmail}
              </Text>
            )}
            {pendingVerifyEmail && "."}
          </Text>
          {resent && (
            <Text fontSize="sm" color="#15803d" mt={3}>
              Confirmation email sent again.
            </Text>
          )}
        </DialogBody>
        <DialogFooter>
          {/* Without a pending address there is nothing to resend to — the
              modal has no input of its own. Hiding the button beats offering
              one that silently does nothing, which is what it did before. */}
          {pendingVerifyEmail && (
            <Button colorPalette="purple" bg="#9333ea" onClick={handleResendLink} loading={isLoading}>
              Resend email confirmation
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  );
};

export default VerifyEmailModal;
